import { Box, Cyl, Lathe, Slab, Stadium } from '../../render/parts'
import { DESK_HEIGHT } from '../parts/worktop'
import type { AssetContext, AssetDefinition } from '../types'

/**
 * Boardroom table: racetrack top on two drum pedestals, with a dark AV / power
 * strip running down the centre line — the detail that reads as "meeting room"
 * at isometric scale.
 *
 * The footprint is declared at the LARGER variant (3.2 x 1.4) so the grid
 * reserves enough room; the 2.4 m top is simply centred inside it.
 */

const HEIGHT = DESK_HEIGHT
const TOP = 0.034
/** Underside of the worktop — everything structural tucks up into this plane. */
const UNDER = HEIGHT - TOP

interface Size {
  width: number
  depth: number
}

const SIZE_M24: Size = { width: 2.4, depth: 1.2 }
const SIZE_M32: Size = { width: 3.2, depth: 1.4 }

const SIZES: Record<string, Size> = {
  m24: SIZE_M24,
  m32: SIZE_M32,
}

/**
 * One drum pedestal: flared disc foot, straight column, and a flat spreader
 * plate that runs across the table depth and sinks 4 mm into the top.
 */
function DrumBase({ spreader }: { spreader: number }) {
  const columnTop = UNDER - 0.014
  const columnBase = 0.05
  return (
    <group>
      <Lathe
        tone="shell"
        segments={40}
        profileKey="table-meeting-drum-foot"
        profile={[
          [0, 0],
          [0.32, 0],
          [0.32, 0.014],
          [0.29, 0.036],
          [0.105, 0.056],
          [0, 0.056],
        ]}
      />
      <Cyl
        tone="shell"
        r={0.09}
        h={columnTop - columnBase}
        segments={28}
        position={[0, (columnTop + columnBase) / 2, 0]}
      />
      {/* Sunk 4 mm into the top so no two faces end up coplanar. */}
      <Box tone="shellSoft" size={[0.16, 0.022, spreader]} position={[0, UNDER - 0.007, 0]} />
    </group>
  )
}

function TableMeeting({ accent, variant = 'm24' }: AssetContext) {
  const { width, depth } = SIZES[variant] ?? SIZE_M24
  const baseX = width * 0.27
  const stripLength = width - 1
  const portX = stripLength / 2 - 0.25

  return (
    <group>
      <Stadium tone="surface" size={[width, depth, TOP]} position={[0, HEIGHT - TOP / 2, 0]} />

      {/* Central AV trough: sunk into the top, standing 2 mm proud of it. */}
      <Slab
        tone="dark"
        size={[stripLength, 0.18, 0.014]}
        radius={0.02}
        position={[0, HEIGHT - 0.005, 0]}
      />
      {[-1, 1].map((side) => (
        <Slab
          key={side}
          tone="accent"
          color={accent}
          size={[0.11, 0.11, 0.005]}
          radius={0.012}
          position={[side * portX, HEIGHT + 0.0035, 0]}
        />
      ))}

      {[-1, 1].map((side) => (
        <group key={side} position={[side * baseX, 0, 0]}>
          <DrumBase spreader={depth - 0.5} />
        </group>
      ))}
    </group>
  )
}

export const definition: AssetDefinition = {
  id: 'table-meeting',
  name: 'Mesa de juntas',
  category: 'tables',
  footprint: [SIZE_M32.width, SIZE_M32.depth],
  height: 0.75,
  surfaceHeight: HEIGHT,
  seats: 6,
  occupantSide: 'around',
  accentable: true,
  variants: [
    { id: 'm24', name: '2.4 m', footprint: [SIZE_M24.width, SIZE_M24.depth] },
    { id: 'm32', name: '3.2 m', footprint: [SIZE_M32.width, SIZE_M32.depth] },
  ],
  keywords: [
    'mesa',
    'juntas',
    'reunión',
    'sala',
    'directorio',
    'meeting',
    'boardroom',
    'conference table',
  ],
  Component: TableMeeting,
}
